import { BottomSheetModal } from '@gorhom/bottom-sheet'
import { useNavigation } from '@react-navigation/native'
import { IS_WEB } from 'constants/platform'
import { PostTypes } from 'constants/postTypes'
import { useTheme } from 'contexts'
import * as Haptics from 'expo-haptics'
import { LinearGradient } from 'expo-linear-gradient'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { StyleSheet } from 'react-native'
import { v4 as uuidv4 } from 'uuid'

import { DeletePostModal } from 'partials/DeletePostModal'
import { PostMediaPreview } from 'partials/EventPostCard/subcomponents/PostMediaPreview'
import { ActionIcon } from 'partials/PostCard/subcomponents'
import { SharePostModal } from 'partials/SharePostModal'
import { WhereToBottomSheet } from 'partials/WhereToBottomSheet'

import { Avatar } from 'widgets/Avatar'
import { Box } from 'widgets/Box'
import { TextButton } from 'widgets/Button'
import { Icon } from 'widgets/Icon'
import { Image } from 'widgets/Image'
import { Modal } from 'widgets/Modal'
import { OptionItem } from 'widgets/OptionsDropdown/subcomponents/OptionItem'
import { DraftOverlay } from 'widgets/PostOverlay'
import { WhereToOverlay } from 'widgets/PostOverlay/WhereToOverlay'
import { Pressable } from 'widgets/Pressable'
import { PressableText } from 'widgets/PressableText'
import { Text } from 'widgets/Text'
import { WhereTo } from 'widgets/WhereTo/WhereTo'

import { useBookmark } from 'hooks/useBookmark'
import { useCompletion } from 'hooks/useCompletion'
import { useCompletionRoom } from 'hooks/useCompletionRoom'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useFollow } from 'hooks/useFollow'
import { useGroup } from 'hooks/useGroup'
import { useGroupsForPost } from 'hooks/useGroupsForPost'
import { useHighFive } from 'hooks/useHighFive'
import { useLike } from 'hooks/useLike'
import { useMaxWidth } from 'hooks/useMaxWidth'
import { usePendingMembership } from 'hooks/usePendingMembership'
import { usePost } from 'hooks/usePost'
import { usePostRoom } from 'hooks/usePostRoom'
import { useUser } from 'hooks/useUser'

import { links } from 'utils/constants'
import { showPendingMembershipSuccess } from 'utils/functions/notifications'
import { handlePostClicked } from 'utils/functions/posts'
import { generateDynamicLink } from 'utils/links'
import {
  copyLinkToClipboard,
  getPostLink,
  getPostLinkMessage,
  openReportForm,
} from 'utils/links/links'
import { defaultPostThumbnailUrl } from 'utils/media'
import { computeMenuOptions } from 'utils/post/post'
import { handleShareAction } from 'utils/sharing'
import commonStyles from 'utils/styles/commonStyles'
import { timeSince } from 'utils/time'

import { useCommentModal } from 'contexts/CommentModalContext'
import { useFocusMode } from 'contexts/FocusModeContext'

import { Event } from 'types/event'
import { Post } from 'types/post'
import { Program } from 'types/program'

import { PostMenu } from './subcomponents/PostMenu'

type EventPostCardProps = {
  eventListIndex: number
  postEvent: Event
  isViewable: boolean
  isMuted: boolean
  setIsMuted: (muted: boolean) => void
  setIsCommentLoading: (isCommentLoading: boolean) => void
}

const styles = StyleSheet.create({
  pressable: {
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
  topGradient: {
    width: '100%',
    height: '20%',
  },
  bottomGradient: {
    width: '100%',
    height: '20%',
    position: 'absolute',
    bottom: 0,
  },
  overflowHidden: {
    overflow: 'hidden',
  },
  bottomSheet: {
    paddingTop: 10,
  },
  topRightOverlayTextContainer: {
    position: 'absolute',
    top: 20,
    right: 20,
    zIndex: 2,
  },
})

export const EventPostCard = ({
  eventListIndex,
  postEvent,
  isViewable,
  isMuted,
  setIsMuted,
  setIsCommentLoading,
}: EventPostCardProps) => {
  const commentModalRef = useCommentModal()
  const focusMode = useFocusMode()
  const isFocusMode = useMemo(() => focusMode.group !== undefined, [focusMode.group])

  const postMediaPreviewRef = useRef<any>(null)

  // eslint-disable-next-line prefer-const
  let { data: postData, isLoading: isPostLoading } = usePost(postEvent.objectId)
  const { data: completionPostData, isLoading: isCompletionPostLoading } = usePost(
    postEvent.post?.id,
  )
  if (postData) {
    postData.id = postEvent.objectId
  } else if (completionPostData) {
    postData = completionPostData
    postData.id = postEvent.post?.id ?? ''
  } else {
    postData = postEvent.post // DEPRECATED: Newer events should not have full post data in them.
  }

  const { postGroups, isLoading: isPostGroupsLoading } = useGroupsForPost(postData)

  const postIdForRoom = postData?.id ?? completionPostData?.id
  const { createRoom: createPostRoom } = usePostRoom({
    postId: postIdForRoom,
  })

  const completionId = postEvent.eventType === 'completion' ? postEvent.objectId : undefined
  const { createRoom: createCompletionRoom } = useCompletionRoom({
    completionId,
  })

  const isActivityCompletionEvent = postData?.type === PostTypes.ACTIVITY
  const isWorkoutCompletionEvent =
    postEvent.eventType === 'completion' && !isActivityCompletionEvent

  const { data: postCreator, isLoading: isPostCreatorLoading } = useUser({
    userId: postEvent?.creator?.id,
  })
  const files = postData?.steps?.filter((item) => item.file !== null && item.file !== undefined)
  const isWorkout = postData?.type === PostTypes.WORKOUT

  const theme = useTheme()
  const navigation = useNavigation<any>()
  const { width: maxWidth } = useMaxWidth()
  const { data: currentUser } = useCurrentUser()

  const { saveBookmark, deleteBookmark, isBookmarked } = useBookmark({
    post: postData ?? ({} as Post),
  })

  let descriptionLinesCount = 0
  const [showSeeMoreButton, setShowSeeMoreButton] = useState(false)
  const [showFullDescription, setShowFullDescription] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isSharePostModalOpen, setIsSharePostModalOpen] = useState(false)

  const { data: completion, isLoading: isCompletionLoading } = useCompletion(postEvent.objectId)

  const { data: following } = useFollow({ trainer: postCreator ?? undefined })

  const {
    data: isCompletionHighFived,
    addHighFive,
    removeHighFive,
  } = useHighFive({
    completionId: postEvent.objectId,
    userId: currentUser?.id,
  })

  const {
    data: isPostLiked,
    likePost,
    dislikePost,
  } = useLike({
    postId: postData?.id ?? postEvent.objectId,
    userId: currentUser?.id,
  })
  const isLiked = isPostLiked && postData && postData.likedCount > 0

  const isOwner = useMemo(
    () => currentUser?.id === postData?.creator?.id,
    [postData?.creator?.id, currentUser?.id],
  )

  const [isOptionsModalVisible, setIsOptionsModalVisible] = useState(false)

  const menuOptions = useCallback(
    () => computeMenuOptions(isOwner, isWorkout),
    [isOwner, isWorkout],
  )

  useEffect(() => {
    if (!isWorkoutCompletionEvent && !isWorkout && files && files.length !== 0 && !isOwner) {
      // playPauseVideo(isViewable)
    }
  }, [files, isOwner, isViewable, isWorkout, isWorkoutCompletionEvent])

  const handleCloseOptionsMenuPress = () => {
    setIsOptionsModalVisible(false)
  }

  const [postLink, setPostLink] = useState('')
  useEffect(() => {
    generateDynamicLink(getPostLink(postData?.id ?? '', postData?.type)).then((dynamicLink) =>
      setPostLink(dynamicLink),
    )
  }, [postData?.id, postData?.type])

  const handleOnSelect = (index: number) => {
    setIsOptionsModalVisible(false)

    switch (index) {
      case 0: {
        if (isOwner) {
          if (isWorkout) {
            handleEditPost()
          } else {
            openDeleteModal()
          }
        } else {
          openReportForm(currentUser, postCreator)
        }
        break
      }
      case 1:
        if (isOwner) {
          handleDuplicatePost()
        } else {
          copyLinkToClipboard(postLink)
        }
        break
      case 2:
        openDeleteModal()
        break
      case 3:
        copyLinkToClipboard(postLink)
        break
      default:
        break
    }
  }

  const handleEditPost = () => {
    navigation.navigate(links.EDIT_WORKOUT_PAGE, {
      postId: postData?.id,
    })
  }

  const handleDuplicatePost = () => {
    navigation.navigate(links.CREATE_WORKOUT_PAGE, {
      postId: postData?.id,
    })
  }

  const handlePostOwnerMenu = () => {
    setIsOptionsModalVisible(true)
  }

  const handlePostViewerMenu = () => {
    setIsOptionsModalVisible(true)
  }

  const openDeleteModal = () => {
    setIsDeleteModalOpen(true)
  }

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false)
  }

  const openSharePostModal = async () => {
    if (!IS_WEB && postData) {
      const hasMedia: boolean = !!files && files.length > 0
      const url = getPostLink(postData.id, postData.type, completionId)
      await handleShareAction(url, getPostLinkMessage(postData.type, hasMedia), navigation)
    } else {
      setIsSharePostModalOpen(true)
    }
  }

  const closeSharePostModal = () => {
    setIsSharePostModalOpen(false)
  }

  const handleBookmarkClicked = () => {
    if (isBookmarked) {
      deleteBookmark.mutateAsync()
    } else {
      saveBookmark.mutateAsync()

      if (!IS_WEB) {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success)
      }
    }
  }

  const handleHighFiveClicked = () => {
    if (isCompletionHighFived) {
      removeHighFive.mutateAsync({ completionId: postEvent.objectId, user: currentUser })
    } else {
      addHighFive.mutateAsync({ completionId: postEvent.objectId, user: currentUser })
    }
  }

  const handleLikeClicked = () => {
    if (isLiked) {
      dislikePost.mutateAsync({ postId: postData?.id, user: currentUser })
    } else {
      likePost.mutateAsync({ postId: postData?.id, user: currentUser })

      if (!IS_WEB) {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success)
      }
    }
  }

  const handleCommentClicked = async () => {
    if (postEvent.eventObject === 'post') {
      setIsCommentLoading(true)
      await createCommentRoom(postEvent.eventType === 'completion')
    }
  }

  const createCommentRoom = useCallback(
    async (isCompletion: boolean) => {
      if ((!isCompletion && !postIdForRoom) || (isCompletion && !completionId)) return

      const createRoom = isCompletion ? createCompletionRoom : createPostRoom
      const { id: roomId } = await createRoom.mutateAsync()

      let routeOptions: any = {
        roomId,
      }
      if (!isCompletion && postIdForRoom) {
        routeOptions = {
          ...routeOptions,
          postId: postIdForRoom,
        }
      }
      if (isCompletion && completionId) {
        routeOptions = {
          ...routeOptions,
          completionId,
        }
      }
      setIsCommentLoading(false)
      commentModalRef?.current?.present({ routeOptions })
    },
    [
      postIdForRoom,
      completionId,
      createCompletionRoom,
      createPostRoom,
      commentModalRef,
      setIsCommentLoading,
    ],
  )

  const { addPendingMembership } = usePendingMembership({
    userId: currentUser?.id,
    programId: postData?.program?.id,
  })

  const { data: postGroup } = useGroup({ groupId: postData?.program?.id })
  const handleAddPendingMembership = () => {
    addPendingMembership.mutate()
    showPendingMembershipSuccess(postData as Post, postGroup as Program)
  }

  const handleSeeMoreClicked = () => {
    setShowFullDescription((value) => !value)
  }

  const shouldShowWhereTo: boolean = useMemo(
    () =>
      !!postData &&
      !postData?.isDraft &&
      !!postData.combinedCategoriesAndProgramIds &&
      postData.combinedCategoriesAndProgramIds?.length > 0 &&
      !!postCreator,
    [postCreator, postData],
  )

  const shouldShowWhereToMeta: boolean = useMemo(
    () =>
      shouldShowWhereTo &&
      !isWorkoutCompletionEvent &&
      !isWorkout &&
      !!postData?.description &&
      !!files &&
      files.length === 0,
    [files, isWorkout, isWorkoutCompletionEvent, postData?.description, shouldShowWhereTo],
  )

  const shouldShowActivityMeta: boolean = useMemo(
    () =>
      (!files || files.length === 0) &&
      isActivityCompletionEvent &&
      !!postData &&
      !!postData?.activityType,
    [files, isActivityCompletionEvent, postData],
  )

  const bottomSheetRef = useRef<BottomSheetModal>(null)

  const isLoading = useMemo(
    () =>
      isPostLoading ||
      isCompletionPostLoading ||
      isPostGroupsLoading ||
      isPostCreatorLoading ||
      isCompletionLoading,
    [
      isCompletionLoading,
      isCompletionPostLoading,
      isPostCreatorLoading,
      isPostLoading,
      isPostGroupsLoading,
    ],
  )

  if (!isWorkout && !postData?.description && !files?.length) {
    return null
  }

  if (postData?.isDraft && postData?.creator?.id !== currentUser?.id) {
    return null
  }

  const navigateToProfile = (username: string | undefined) => {
    navigation.navigate(!isFocusMode ? links.PROFILE_STACK : links.GROUP_STACK, {
      screen: links.MEMBER_PROFILE_PAGE,
      params: { username },
    })
  }

  const showCommentButton =
    (postEvent.eventObject === 'post' || postEvent.eventType === 'completion') &&
    (isOwner || following)
  const postCommentCount = postData?.commentsCount ? `${postData?.commentsCount}` : ''
  const completionCommentCount = completion?.commentsCount ? `${completion?.commentsCount}` : ''
  const commentButtonTitle =
    postEvent.eventType === 'completion' ? completionCommentCount : postCommentCount

  // if (isLoading && isWorkoutCompletionEvent) {
  //   return <CompletionPlaceholder />
  // }
  //
  // if (isLoading && isActivityCompletionEvent) {
  //   return <ActivityPostPlaceholder />
  // }
  //
  // if (isLoading && !isWorkout) {
  //   return <TextPostPlaceholder />
  // }
  //
  // if (isLoading && isWorkout) {
  //   return <WorkoutPlaceholder />
  // }

  return (
    <Box margin={{ bottom: 24 }}>
      <DeletePostModal
        handleClose={closeDeleteModal}
        isOpen={isDeleteModalOpen}
        postId={postData?.id ?? ''}
      />

      <SharePostModal
        handleClose={closeSharePostModal}
        isOpen={isSharePostModalOpen}
        postId={postData?.id ?? ''}
        postType={postData?.type}
        completionId={completionId}
        testID={`share-post-modal-${postData?.id}`}
      />

      <Box align='flex-start' flexDirection='row' margin={{ vertical: 'sm', horizontal: 'md' }}>
        <Box testID='AvatarColumn' width={33} margin={{ right: 'sm' }}>
          <Pressable onPress={() => navigateToProfile(postCreator?.username)}>
            <Avatar width={33} height={33} image={postCreator?.avatar} />
          </Pressable>
        </Box>

        <Box testID='ContentColumn' flex={1} margin={{ top: 2 }}>
          <Box testID='UsernameRow' flexDirection='row' align='center' justifyContent='center'>
            <Pressable onPress={() => navigateToProfile(postCreator?.username)}>
              <Text bold color='black'>
                {postCreator?.username}
              </Text>
            </Pressable>
            <Box flex={1} />
            {isActivityCompletionEvent && postData?.activityGroup && (
              <Box flexDirection='row' margin={{ horizontal: 'sm' }} align='center'>
                <Icon name='check' color='v2primary' size={20} />
                <Text bold fontSize='sm' color='greyDark'>
                  {postData.activityGroup.charAt(0).toUpperCase() + postData.activityGroup.slice(1)}
                </Text>
              </Box>
            )}
            {isWorkoutCompletionEvent && (
              <Box flexDirection='row' margin={{ horizontal: 'sm' }} align='center'>
                <Icon name='check' color='v2primary' size={20} />
                <Text bold fontSize='sm' color='greyDark'>
                  Workout
                </Text>
              </Box>
            )}
            <Text fontSize='sm' color='greyDark'>
              {postData && timeSince(postEvent.created)}
            </Text>
          </Box>

          {isWorkoutCompletionEvent && ( // Workout completion event
            <Box flexDirection='row' margin={{ top: 'sm' }}>
              <Pressable
                onPress={() =>
                  handlePostClicked(
                    isOwner,
                    navigation,
                    () => handleAddPendingMembership,
                    postData as Post,
                    completionId,
                  )
                }
              >
                <Box width={50} height={50} borderRadius='sm' style={styles.overflowHidden}>
                  <Image
                    cacheKey={postData ? `Post-${postData?.id}-thumb` : uuidv4()}
                    resizeMode='cover'
                    style={commonStyles.fullSize}
                    source={{ uri: postData?.thumbnail?.url ?? defaultPostThumbnailUrl.url }}
                  />
                  <Box
                    testID='GradientContainer'
                    pointerEvents='none'
                    zIndex={1}
                    position='absolute'
                    display='flex'
                    width='100%'
                    height='100%'
                    flexDirection='column'
                    justifyContent='space-between'
                  >
                    <LinearGradient
                      style={commonStyles.fullHeight}
                      colors={[theme.colors.black20, theme.colors.black20]}
                      start={{ x: 0, y: 1 }}
                      end={{ x: 0, y: 0 }}
                    />
                  </Box>
                  <Box
                    position='absolute'
                    zIndex={2}
                    align='center'
                    justifyContent='center'
                    margin={{ left: 15, top: 15 }}
                  >
                    <Box
                      backgroundColor='v2primary'
                      borderRadius='lg'
                      border={{ color: 'white', width: 2 }}
                      width={20}
                      height={20}
                    >
                      <Box margin={{ top: 3, left: 3 }}>
                        <Icon name='check' color='white' size={10} />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Pressable>

              <Box flex={1} margin={{ left: 'md' }} justifyContent='center'>
                <Pressable
                  onPress={() =>
                    handlePostClicked(
                      isOwner,
                      navigation,
                      () => handleAddPendingMembership,
                      postData as Post,
                      completionId,
                    )
                  }
                >
                  <Box>
                    <PressableText
                      numberOfLines={1}
                      bold
                      fontSize='md'
                      onPress={() =>
                        handlePostClicked(
                          isOwner,
                          navigation,
                          () => handleAddPendingMembership,
                          postData as Post,
                        )
                      }
                    >
                      {postData?.title}
                    </PressableText>
                    <PressableText
                      numberOfLines={1}
                      fontSize='sm'
                      onPress={() => navigateToProfile(postData?.creator.username)}
                    >
                      {postData?.creator.username
                        ? `${postData?.creator.username}`
                        : `${postData?.creator.firstName} ${postData?.creator.lastName}`}
                    </PressableText>
                  </Box>
                </Pressable>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {(shouldShowWhereToMeta || shouldShowActivityMeta) && (
        <Box
          testID='MetaRow'
          flexDirection='row'
          align='center'
          justifyContent={!shouldShowWhereToMeta ? 'flex-end' : 'space-between'}
          margin={{ horizontal: 'md', bottom: 'sm' }}
        >
          <Box testID='WhereToMetaColumn'>
            {shouldShowWhereToMeta && (
              <WhereTo
                combinedCategoriesAndProgramIds={postData?.combinedCategoriesAndProgramIds}
                creator={postCreator}
                bottomSheetRef={bottomSheetRef}
              />
            )}
          </Box>
          {shouldShowActivityMeta && (
            <Box
              testID='ActivityGroupColumn'
              justifyContent='flex-end'
              align='center'
              margin={{ bottom: 'sm', top: 'sm' }}
            >
              <Text fontSize='sm'>
                Logged{' '}
                <Text bold={postData.activityType !== '+ Other'} fontSize='sm'>
                  {postData.activityType === '+ Other' ? 'an' : postData.activityType}
                </Text>{' '}
                activity
              </Text>
            </Box>
          )}
        </Box>
      )}

      {!isWorkoutCompletionEvent && postData?.description ? ( // Post description
        <Box
          testID='DescriptionTextRow'
          flexDirection='row'
          margin={{ bottom: 'sm', horizontal: 'md' }}
        >
          <Box flex={1} testID='DescriptionTextColumn'>
            <Text
              onTextLayout={(event) => {
                if (event.nativeEvent.lines.length > descriptionLinesCount) {
                  descriptionLinesCount = event.nativeEvent.lines.length
                  setShowSeeMoreButton(descriptionLinesCount >= 6)
                }
              }}
              withLinks
              ellipsizeMode={!showFullDescription ? 'tail' : undefined}
              numberOfLines={!showFullDescription ? 6 : undefined}
              fontSize='sm'
            >
              {postData?.description}
            </Text>
            {(IS_WEB &&
              (postData?.description.length > 116 ||
                (postData?.description.match(/\n/g) || []).length >= 5)) ||
            (!IS_WEB && (showSeeMoreButton || showFullDescription)) ? (
              <Box align='flex-end'>
                <TextButton
                  size='sm'
                  bold={false}
                  underline={false}
                  title={`See ${showFullDescription ? 'less' : 'more'}`}
                  onPress={handleSeeMoreClicked}
                  type='greyDark'
                />
              </Box>
            ) : null}
          </Box>
        </Box>
      ) : null}

      <Box flexDirection='row' width='100%'>
        {!isWorkoutCompletionEvent && isWorkout && postData && (
          <Box
            width='100%'
            height={maxWidth * 1.75}
            margin={{ bottom: 'sm' }}
            style={styles.overflowHidden}
          >
            <Box
              testID='PostImageContainer'
              zIndex={1}
              position='absolute'
              display='flex'
              width='100%'
              height='100%'
            >
              {postData?.isDraft && <DraftOverlay />}
              {shouldShowWhereTo && (
                <WhereToOverlay
                  combinedCategoriesAndProgramIds={postData.combinedCategoriesAndProgramIds}
                  creator={postCreator}
                  bottomSheetRef={bottomSheetRef}
                />
              )}
              {!isWorkoutCompletionEvent && isWorkout && (
                <Box style={styles.topRightOverlayTextContainer}>
                  <Text
                    fontSize='sm'
                    fontWeight='bold'
                    color='textInverted'
                    style={commonStyles.shadowText}
                  >
                    New Workout
                  </Text>
                </Box>
              )}

              <Image
                cacheKey={postData ? `Post-${postData?.id}-thumb` : uuidv4()}
                resizeMode='cover'
                style={commonStyles.fullSize}
                source={{ uri: postData?.thumbnail?.url ?? defaultPostThumbnailUrl.url }}
              />

              <Pressable
                style={styles.pressable}
                onPress={() =>
                  handlePostClicked(
                    isOwner,
                    navigation,
                    () => handleAddPendingMembership,
                    postData as Post,
                    isActivityCompletionEvent ? completionId : undefined,
                  )
                }
              />
            </Box>

            <Box
              testID='GradientContainer'
              pointerEvents='none'
              zIndex={1}
              position='absolute'
              display='flex'
              width='100%'
              height='100%'
              flexDirection='column'
              justifyContent='space-between'
            >
              <LinearGradient
                style={styles.bottomGradient}
                colors={[theme.colors.black80, '#00000000']}
                start={{ x: 0, y: 1 }}
                end={{ x: 0, y: 0 }}
              />
            </Box>

            <Box testID='Spacer' flex={1} />

            <Box
              testID='BottomContentContainer'
              pointerEvents='box-none'
              zIndex={2}
              padding={{ horizontal: 'md', bottom: 'md' }}
              display='flex'
              flexDirection='row'
              align='center'
              justifyContent='space-between'
            >
              <Box padding={{ horizontal: 'sm' }} flex={1}>
                <Text
                  align='left'
                  fontSize='lg'
                  fontWeight='bold'
                  color='textInverted'
                  style={commonStyles.shadowText}
                >
                  {postData?.title}
                </Text>
              </Box>
            </Box>
          </Box>
        )}

        {!isWorkoutCompletionEvent &&
          !isWorkout &&
          files &&
          files.length !== 0 && ( // Post images
            <Box
              height={maxWidth * 1.25}
              margin={{ bottom: 'sm' }}
              style={styles.overflowHidden}
              width='100%'
            >
              {postData?.isDraft && <DraftOverlay />}
              {shouldShowWhereTo && (
                <WhereToOverlay
                  combinedCategoriesAndProgramIds={postData?.combinedCategoriesAndProgramIds}
                  creator={postCreator}
                  bottomSheetRef={bottomSheetRef}
                />
              )}
              {isActivityCompletionEvent && !!postData && !!postData?.activityType && (
                <Box
                  testID='ActivityGroupColumn'
                  justifyContent='flex-end'
                  align='center'
                  style={styles.topRightOverlayTextContainer}
                >
                  <Text fontSize='sm' color='textInverted' style={commonStyles.shadowText}>
                    Logged{' '}
                    <Text
                      color='textInverted'
                      bold={postData.activityType !== '+ Other'}
                      fontSize='sm'
                    >
                      {postData.activityType === '+ Other' ? 'an' : postData.activityType}
                    </Text>{' '}
                    activity
                  </Text>
                </Box>
              )}

              <PostMediaPreview
                ref={postMediaPreviewRef}
                files={files}
                isOwner={isOwner}
                autoplay={false}
                muted={isMuted}
                setIsMuted={setIsMuted}
                tapped={() => {
                  postMediaPreviewRef?.current?.pausePlaying()
                  handlePostClicked(
                    isOwner,
                    navigation,
                    () => handleAddPendingMembership,
                    postData as Post,
                    isActivityCompletionEvent ? completionId : undefined,
                  )
                }}
              />
            </Box>
          )}
      </Box>

      <Box flexDirection='row' justifyContent='space-between' margin={{ horizontal: 'md' }}>
        <Box testID='MediaRow' flexDirection='row' align='center' justifyContent='space-between'>
          <Box testID=''>
            {isWorkoutCompletionEvent &&
              !isWorkout &&
              files &&
              files.length !== 0 && ( // Post images
                <Box
                  height={maxWidth}
                  borderRadius='sm'
                  margin={{ bottom: 'sm' }}
                  style={styles.overflowHidden}
                >
                  {postData?.isDraft && <DraftOverlay />}
                  {shouldShowWhereTo && (
                    <WhereToOverlay
                      combinedCategoriesAndProgramIds={postData?.combinedCategoriesAndProgramIds}
                      creator={postCreator}
                      bottomSheetRef={bottomSheetRef}
                    />
                  )}

                  <PostMediaPreview
                    ref={postMediaPreviewRef}
                    files={files}
                    isOwner={isOwner}
                    autoplay={false}
                    muted={isMuted}
                    setIsMuted={setIsMuted}
                    tapped={() => {
                      postMediaPreviewRef?.current?.pausePlaying()
                      handlePostClicked(
                        isOwner,
                        navigation,
                        () => handleAddPendingMembership,
                        postData as Post,
                        isActivityCompletionEvent ? completionId : undefined,
                      )
                    }}
                  />
                </Box>
              )}
            <Box
              testID='ActionButtonRow'
              flexDirection='row'
              align='center'
              justifyContent='space-between'
            >
              {isWorkoutCompletionEvent && (
                <ActionIcon
                  direction='row'
                  iconSize={24}
                  icon='upspace-logo-mark'
                  color={isCompletionHighFived ? 'v2primary' : 'black'}
                  textColor='black'
                  label={completion?.highFivedCount?.toString() ?? `${0}`}
                  action={handleHighFiveClicked}
                />
              )}
              {!isWorkoutCompletionEvent && (
                <ActionIcon
                  direction='row'
                  iconSize={24}
                  icon={isLiked ? 'heart-filled' : 'heart'}
                  color={isLiked ? 'v2secondary' : 'black'}
                  textColor='black'
                  label={postData?.likedCount !== undefined ? `${postData?.likedCount}` : ''}
                  action={handleLikeClicked}
                />
              )}
              {showCommentButton && (
                <>
                  <Box margin='sm' />
                  <ActionIcon
                    direction='row'
                    iconSize={24}
                    icon='message-circle'
                    color='black'
                    label={commentButtonTitle}
                    action={handleCommentClicked}
                  />
                </>
              )}
              {!isWorkoutCompletionEvent && !isActivityCompletionEvent && (
                <>
                  <Box margin='sm' />
                  <ActionIcon
                    direction='row'
                    iconSize={24}
                    icon={isBookmarked ? 'bookmark-filled' : 'bookmark'}
                    color={isBookmarked ? 'v2primary' : 'black'}
                    textColor='black'
                    label=''
                    action={handleBookmarkClicked}
                  />
                </>
              )}
              <Box margin='sm' />
              <ActionIcon
                direction='row'
                iconSize={24}
                icon='share'
                color='black'
                label=''
                action={openSharePostModal}
              />
            </Box>
          </Box>
        </Box>
        <Box testID='PostMenuColumn'>
          <PostMenu
            mode='dark'
            isOwner={isOwner}
            handlePostOwnerMenu={handlePostOwnerMenu}
            handlePostViewerMenu={handlePostViewerMenu}
          />
        </Box>
      </Box>

      <Modal
        isVisible={isOptionsModalVisible}
        onClose={handleCloseOptionsMenuPress}
        variant='fullscreenModal'
      >
        {menuOptions().map((option, index) => {
          return (
            <OptionItem
              key={option}
              option={option}
              isCancelItem={false}
              onSelect={() => {
                handleOnSelect(index)
              }}
            />
          )
        })}
        <OptionItem isCancelItem onSelect={handleCloseOptionsMenuPress} />
      </Modal>

      {shouldShowWhereTo && (
        <WhereToBottomSheet
          bottomSheetRef={bottomSheetRef}
          creator={postCreator}
          navigation={navigation}
          programs={postGroups}
        />
      )}
    </Box>
  )
}
